import { h, FunctionalComponent } from "preact";
import { useMemo, useEffect, useRef } from "preact/hooks";
import { Marker, useMap, MarkerProps } from "react-leaflet";
import L, { LatLng, LeafletEventHandlerFnMap } from "leaflet";
import type MvArea from "models/MvArea";
interface CustomMarkerProps extends Omit<MarkerProps, "eventHandlers"> {
    area: MvArea;
    onSelect(area: MvArea): void;
    onPositionUpdate(newPos: LatLng, area: MvArea): void;
}

const CustomMarker: FunctionalComponent<CustomMarkerProps> = ({
    area,
    onSelect,
    onPositionUpdate,
    ...restProps
}: CustomMarkerProps) => {
    const map = useMap();
    const markerRef = useRef<L.Marker>(null);
    const eventHandlers: LeafletEventHandlerFnMap = useMemo(
        () => ({
            mouseover() {
                if (
                    L.DomUtil.hasClass(
                        map.getContainer(),
                        "crosshair-cursor-enabled",
                    )
                ) {
                    L.DomUtil.removeClass(
                        map.getContainer(),
                        "crosshair-cursor-enabled",
                    );
                }
            },
            mouseout() {
                if (
                    !L.DomUtil.hasClass(
                        map.getContainer(),
                        "crosshair-cursor-enabled",
                    )
                ) {
                    L.DomUtil.addClass(
                        map.getContainer(),
                        "crosshair-cursor-enabled",
                    );
                }
            },
            dragend() {
                const marker = markerRef.current;
                if (marker != null) {
                    onPositionUpdate(marker.getLatLng(), area);
                }
            },
            click: () => {
                onSelect(area);
            },
        }),
        [area, onSelect, onPositionUpdate, map],
    );
    useEffect(() => {
        if (area.isSelected) {
            markerRef.current?.openPopup();
        } else {
            markerRef.current?.closePopup();
        }
    }, [area.isSelected]);
    return (
        <Marker ref={markerRef} eventHandlers={eventHandlers} {...restProps} />
    );
};

export default CustomMarker;
